import React, { useState } from 'react';
import PasswordInput from './PasswordInput';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onChangePassword: (oldPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  t: (key: string) => string;
  styles: any;
  dir?: 'ltr' | 'rtl';
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
  onChangePassword,
  t,
  styles,
  dir = 'ltr'
}) => {
  const [changePasswordData, setChangePasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!changePasswordData.oldPassword || !changePasswordData.newPassword || !changePasswordData.confirmPassword) {
      alert(t('fillAllFields'));
      return;
    }
    
    if (changePasswordData.newPassword !== changePasswordData.confirmPassword) {
      alert(t('passwordMismatch'));
      return;
    }
    
    if (changePasswordData.newPassword.length < 6) {
      alert(t('passwordTooShort'));
      return;
    }
    
    setChangingPassword(true);
    try {
      const response = await onChangePassword(
        changePasswordData.oldPassword,
        changePasswordData.newPassword
      );
      
      if (response.success) {
        alert(t('passwordChangedSuccess'));
        closeModal();
      } else {
        alert(response.error || t('errorChangingPassword'));
      }
    } catch (error) {
      console.error('Erreur lors du changement de mot de passe:', error);
      alert(t('errorChangingPassword'));
    } finally {
      setChangingPassword(false);
    }
  };

  const closeModal = () => {
    setChangePasswordData({
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={styles.modal}>
      <div style={styles.modalContent}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
            {t('changePassword')}
          </h2>
          <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t('oldPassword')} *</label>
              <PasswordInput
                value={changePasswordData.oldPassword}
                onChange={(e) => setChangePasswordData({...changePasswordData, oldPassword: e.target.value})}
                placeholder={t('enterOldPassword')}
                dir={dir}
                style={styles.input}
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t('newPassword')} *</label>
              <PasswordInput
                value={changePasswordData.newPassword}
                onChange={(e) => setChangePasswordData({...changePasswordData, newPassword: e.target.value})}
                placeholder={t('enterNewPassword')}
                dir={dir}
                style={styles.input}
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t('confirmPassword')} *</label>
              <PasswordInput
                value={changePasswordData.confirmPassword}
                onChange={(e) => setChangePasswordData({...changePasswordData, confirmPassword: e.target.value})}
                placeholder={t('confirmNewPassword')}
                dir={dir}
                style={styles.input}
                required
              />
            </div>
          <div style={styles.buttonGroup}>
            <button
              type="button"
              onClick={closeModal}
              disabled={changingPassword}
              style={{
                ...styles.button,
                ...styles.secondaryButton,
                ...styles.flexButton,
                opacity: changingPassword ? 0.6 : 1,
                cursor: changingPassword ? 'not-allowed' : 'pointer'
              }}
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              disabled={changingPassword}
              style={{
                ...styles.button,
                ...styles.primaryButton,
                ...styles.flexButton,
                opacity: changingPassword ? 0.6 : 1,
                cursor: changingPassword ? 'not-allowed' : 'pointer'
              }}
            >
              {changingPassword ? t('changing') : t('changePassword')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordModal;

